/**
 * @file Transfer Value Modal View Component
 * @remarks
 * View component that displays Gemini Player Valuation with visual gradient chart.
 * Shows Lower Bound/Upper Bound values and optional public valuation marker.
 * Uses ScopedModal to render within player content area via portal.
 */

import { LinearGradient } from "expo-linear-gradient";
import { memo } from "react";
import {
  type DimensionValue,
  type TextStyle,
  type ViewStyle,
} from "react-native";

import ScopedModal from "@/components/organisms/shared/ScopedModal";
import { Box } from "@/components/atoms/Box";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";

import { CurrencyCode } from "@/generated/graphql";
import formatCurrencyShort from "@/utils/formatCurrencyShort";
import { RADIUS } from "@/config/radius";

/**
 * Props for the TransferValueModalView component
 */
interface TransferValueModalViewProps {
  isOpen: boolean;
  onClose: () => void;
  playerName: string;
  currencyCode: CurrencyCode;
  isDark: boolean;
  geminiMin: number | null | undefined;
  geminiMax: number | null | undefined;
  playerValuation: number | null | undefined;
  minPosition: number;
  maxPosition: number;
  adjustedMinPosition?: number;
  adjustedMaxPosition?: number;
  publicPosition: number;
  greenColor: string;
  orangeColor: string;
  blueColor: string;
}

/**
 * Calculate marker position as percentage on the scale
 * @param value - The value to position
 * @param min - Minimum value for scale
 * @param max - Maximum value for scale
 * @returns Percentage position (0-100)
 */
const calculatePosition = (
  value: number | null | undefined,
  min: number,
  max: number
): number => {
  if (value == null || max === min) return 50;
  const position = ((value - min) / (max - min)) * 100;
  return Math.max(5, Math.min(95, position));
};

/**
 * Calculate scale boundaries from a list of values
 * @param values - Array of values to calculate scale from
 * @returns Object with scaleMin and scaleMax values
 */
const calculateScaleBoundaries = (
  values: (number | null | undefined)[]
): { scaleMin: number; scaleMax: number } => {
  const validValues = values.filter((v): v is number => v != null);
  const scaleMin = validValues.length > 0 ? Math.min(...validValues) * 0.8 : 0;
  const scaleMax =
    validValues.length > 0 ? Math.max(...validValues) * 1.2 : 100;
  return { scaleMin, scaleMax };
};

/**
 * Get theme-appropriate colors for the transfer value indicators
 * @param isDark - Whether dark mode is active
 * @returns Object with green, orange, and blue color values
 */
const getIndicatorColors = (
  isDark: boolean
): { greenColor: string; orangeColor: string; blueColor: string } => ({
  greenColor: isDark ? "#4ade80" : "#22c55e",
  orangeColor: isDark ? "#fb923c" : "#f97316",
  blueColor: isDark ? "#60a5fa" : "#3b82f6",
});

/**
 * Style for the gradient bar's fixed geometry.
 * @remarks
 * design-system: dynamic-style waiver — expo-linear-gradient is not
 * NativeWind-interop-registered, so closed-token classNames (h-2 / rounded-sm)
 * cannot reach its style prop. Hoisted to a const so no inline style literal
 * remains. Values map 1:1 to the closed tokens h-2 (8px) + rounded-sm (4px).
 */
const gradientBarStyle: ViewStyle = { height: 8, borderRadius: RADIUS.sm };

/**
 * Build the inline style for a Gemini bound label (Lower/Upper) container.
 * @param leftPercent - Computed horizontal position as a CSS percentage string
 * @returns Absolute-position style with width-coupled centering offset
 * @remarks
 * design-system: dynamic-style waiver — computed marker position (% left) with
 * a width-coupled centering offset (marginLeft = -width/2) and an off-scale
 * 90px fixed label width. Annotated ViewStyle for the Stack atom UNSAFE_style
 * consumer; `left` typed DimensionValue so the string percentage is accepted.
 */
const boundLabelStyle = (leftPercent: DimensionValue): ViewStyle => ({
  left: leftPercent,
  marginLeft: -45,
  width: 90,
});

/**
 * Build the inline style for the public-valuation label container.
 * @param leftPercent - Computed horizontal position as a CSS percentage string
 * @returns Absolute-position style with width-coupled centering offset
 * @remarks
 * design-system: dynamic-style waiver — computed marker position (% left) with
 * a width-coupled centering offset (marginLeft = -width/2) and an off-scale
 * 100px fixed label width. Annotated ViewStyle for the Stack atom UNSAFE_style
 * consumer; `left` typed DimensionValue so the string percentage is accepted.
 */
const publicLabelStyle = (leftPercent: DimensionValue): ViewStyle => ({
  left: leftPercent,
  marginLeft: -50,
  width: 100,
});

/**
 * Build the inline style for a triangular position marker glyph.
 * @param leftPercent - Computed horizontal position as a CSS percentage string
 * @param color - Data-driven theme color for the marker
 * @returns Absolute-position text style with centering offset and color
 * @remarks
 * design-system: dynamic-style waiver — computed marker position (% left),
 * centering offset (marginLeft), and data-driven theme color. Annotated
 * TextStyle because the string `left` dimension needs contextual typing on the
 * raw-RN Text consumer.
 */
const markerStyle = (
  leftPercent: DimensionValue,
  color: string
): TextStyle => ({
  left: leftPercent,
  marginLeft: -10,
  color,
});

/**
 * Build the inline style for a data-driven text color.
 * @param color - Theme-selected color (dark-mode-dependent hex from props)
 * @returns Text style carrying only the dynamic color
 * @remarks
 * design-system: dynamic-style waiver — data-driven theme color (greenColor /
 * orangeColor / blueColor props). The exact dark-mode hex values are not
 * representable by the closed semantic palette without a visual shift, so the
 * color stays inline; hoisted to a helper so no style literal remains.
 */
const textColorStyle = (color: string): TextStyle => ({ color });

/**
 * View component that renders a simplified transfer value modal with Gemini valuations
 * @param root0 - Component props
 * @param root0.isOpen - Boolean flag indicating if the modal is currently visible
 * @param root0.onClose - Callback function invoked when the modal is closed
 * @param root0.playerName - Name of the player being evaluated
 * @param root0.currencyCode - Currency code for formatting monetary values
 * @param root0.isDark - Boolean flag indicating if dark mode is active
 * @param root0.geminiMin - Lower bound of Gemini Player Valuation
 * @param root0.geminiMax - Upper bound of Gemini Player Valuation
 * @param root0.playerValuation - Current public market valuation of the player
 * @param root0.minPosition - Pre-calculated position percentage for Lower Bound marker
 * @param root0.maxPosition - Pre-calculated position percentage for Upper Bound marker
 * @param root0.adjustedMinPosition - Overlap-adjusted label position for Lower Bound
 * @param root0.adjustedMaxPosition - Overlap-adjusted label position for Upper Bound
 * @param root0.publicPosition - Pre-calculated position percentage for public valuation marker
 * @param root0.greenColor - Theme-appropriate green color for Lower Bound
 * @param root0.orangeColor - Theme-appropriate orange color for Upper Bound
 * @param root0.blueColor - Theme-appropriate blue color for public valuation
 */
const TransferValueModalView = ({
  isOpen,
  onClose,
  playerName,
  currencyCode,
  isDark,
  geminiMin,
  geminiMax,
  playerValuation,
  minPosition,
  maxPosition,
  adjustedMinPosition,
  adjustedMaxPosition,
  publicPosition,
  greenColor,
  orangeColor,
  blueColor,
}: TransferValueModalViewProps) => (
  <ScopedModal
    isOpen={isOpen}
    onClose={onClose}
    title="Gemini Player Valuation"
    isDark={isDark}
    maxWidth={500}
    maxHeightPercent={80}
  >
    <Stack className="gap-6">
      {/* Player Name */}
      <Text
        variant="title"
        className={isDark ? "text-white" : "text-content-primary"}
      >
        {playerName}
      </Text>

      {/* Gemini Player Valuation Section */}
      <Stack className="gap-4">
        <Text
          variant="title-sm"
          // eslint-disable-next-line no-restricted-syntax -- data-driven theme color hex prop; no closed semantic token equivalent
          UNSAFE_style={
            textColorStyle(greenColor) as Record<string, string | number>
          }
        >
          Gemini Player Valuation
        </Text>

        {/* Lower Bound Row */}
        <Stack
          direction="horizontal"
          className="items-center justify-between border-b border-outline-subtle py-3"
        >
          <Text variant="body" className="text-content-primary">
            Lower Bound
          </Text>
          <Text variant="title" className="text-content-primary">
            {geminiMin != null
              ? formatCurrencyShort(geminiMin, currencyCode)
              : "—"}
          </Text>
        </Stack>

        {/* Upper Bound Row */}
        <Stack
          direction="horizontal"
          className="items-center justify-between border-b border-outline-subtle py-3"
        >
          <Text variant="body" className="text-content-primary">
            Upper Bound
          </Text>
          <Text variant="title" className="text-content-primary">
            {geminiMax != null
              ? formatCurrencyShort(geminiMax, currencyCode)
              : "—"}
          </Text>
        </Stack>
      </Stack>

      {/* Visual Chart — show when any valuation data exists */}
      {(geminiMin != null || geminiMax != null || playerValuation != null) && (
        <Stack className="gap-1 pt-2">
          {/* Labels and marker triangles above bar (only when bounds exist) */}
          {(geminiMin != null || geminiMax != null) && (
            <>
              <Box className="relative h-10 w-full">
                {/* Lower Bound label */}
                {geminiMin != null && (
                  <Stack
                    className="absolute items-center"
                    // eslint-disable-next-line no-restricted-syntax -- computed % marker position + width-coupled centering offset + off-scale label width
                    UNSAFE_style={
                      boundLabelStyle(
                        `${adjustedMinPosition ?? minPosition}%`
                      ) as Record<string, string | number>
                    }
                  >
                    <Text
                      variant="unstyled"
                      className="text-center font-medium text-micro"
                      // eslint-disable-next-line no-restricted-syntax -- data-driven theme color hex prop; no closed semantic token equivalent
                      UNSAFE_style={
                        textColorStyle(greenColor) as Record<
                          string,
                          string | number
                        >
                      }
                    >
                      Lower Bound
                    </Text>
                    <Text
                      variant="caption-strong"
                      className="text-center"
                      // eslint-disable-next-line no-restricted-syntax -- data-driven theme color hex prop; no closed semantic token equivalent
                      UNSAFE_style={
                        textColorStyle(greenColor) as Record<
                          string,
                          string | number
                        >
                      }
                    >
                      {formatCurrencyShort(geminiMin, currencyCode)}
                    </Text>
                  </Stack>
                )}
                {/* Upper Bound label */}
                {geminiMax != null && (
                  <Stack
                    className="absolute items-center"
                    // eslint-disable-next-line no-restricted-syntax -- computed % marker position + width-coupled centering offset + off-scale label width
                    UNSAFE_style={
                      boundLabelStyle(
                        `${adjustedMaxPosition ?? maxPosition}%`
                      ) as Record<string, string | number>
                    }
                  >
                    <Text
                      variant="unstyled"
                      className="text-center font-medium text-micro"
                      // eslint-disable-next-line no-restricted-syntax -- data-driven theme color hex prop; no closed semantic token equivalent
                      UNSAFE_style={
                        textColorStyle(orangeColor) as Record<
                          string,
                          string | number
                        >
                      }
                    >
                      Upper Bound
                    </Text>
                    <Text
                      variant="caption-strong"
                      className="text-center"
                      // eslint-disable-next-line no-restricted-syntax -- data-driven theme color hex prop; no closed semantic token equivalent
                      UNSAFE_style={
                        textColorStyle(orangeColor) as Record<
                          string,
                          string | number
                        >
                      }
                    >
                      {formatCurrencyShort(geminiMax, currencyCode)}
                    </Text>
                  </Stack>
                )}
              </Box>

              {/* Marker triangles above bar */}
              <Box className="relative h-4 w-full">
                {geminiMin != null && (
                  <Text
                    variant="unstyled"
                    className="absolute text-title-lg"
                    // eslint-disable-next-line no-restricted-syntax -- computed % marker position + centering offset + data-driven theme color
                    UNSAFE_style={
                      markerStyle(`${minPosition}%`, greenColor) as Record<
                        string,
                        string | number
                      >
                    }
                  >
                    ▼
                  </Text>
                )}
                {geminiMax != null && (
                  <Text
                    variant="unstyled"
                    className="absolute text-title-lg"
                    // eslint-disable-next-line no-restricted-syntax -- computed % marker position + centering offset + data-driven theme color
                    UNSAFE_style={
                      markerStyle(`${maxPosition}%`, orangeColor) as Record<
                        string,
                        string | number
                      >
                    }
                  >
                    ▼
                  </Text>
                )}
              </Box>
            </>
          )}

          {/* Gradient Bar */}
          <LinearGradient
            colors={["#22c55e", "#eab308", "#f97316", "#ef4444"]}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 0 }}
            // eslint-disable-next-line no-restricted-syntax -- expo-linear-gradient not NativeWind-registered; h-2/rounded-sm cannot reach its style prop
            style={gradientBarStyle}
          />

          {/* Public Valuation marker below bar */}
          {playerValuation != null && (
            <>
              <Box className="relative h-4 w-full">
                <Text
                  variant="unstyled"
                  className="absolute text-title-lg"
                  // eslint-disable-next-line no-restricted-syntax -- computed % marker position + centering offset + data-driven theme color
                  UNSAFE_style={
                    markerStyle(`${publicPosition}%`, blueColor) as Record<
                      string,
                      string | number
                    >
                  }
                >
                  ▲
                </Text>
              </Box>
              <Box className="relative h-10 w-full">
                <Stack
                  className="absolute items-center"
                  // eslint-disable-next-line no-restricted-syntax -- computed % marker position + width-coupled centering offset + off-scale label width
                  UNSAFE_style={
                    publicLabelStyle(`${publicPosition}%`) as Record<
                      string,
                      string | number
                    >
                  }
                >
                  <Text
                    variant="unstyled"
                    className="text-center font-medium text-micro"
                    // eslint-disable-next-line no-restricted-syntax -- data-driven theme color hex prop; no closed semantic token equivalent
                    UNSAFE_style={
                      textColorStyle(blueColor) as Record<
                        string,
                        string | number
                      >
                    }
                  >
                    Public Valuation
                  </Text>
                  <Text
                    variant="caption-strong"
                    className="text-center"
                    // eslint-disable-next-line no-restricted-syntax -- data-driven theme color hex prop; no closed semantic token equivalent
                    UNSAFE_style={
                      textColorStyle(blueColor) as Record<
                        string,
                        string | number
                      >
                    }
                  >
                    {formatCurrencyShort(playerValuation, currencyCode)}
                  </Text>
                </Stack>
              </Box>
            </>
          )}
        </Stack>
      )}
    </Stack>
  </ScopedModal>
);

TransferValueModalView.displayName = "TransferValueModalView";

export default memo(TransferValueModalView);

export { calculatePosition, calculateScaleBoundaries, getIndicatorColors };
